import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Clock, CheckCircle2, XCircle, AlertCircle, Loader2,
  Send, Image as ImageIcon, Star, Flag, Shield,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import CounterpartyCard from "@/components/p2p/CounterpartyCard";
import AppealView from "@/components/p2p/AppealView";
import { api, wsBaseUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * P2POrderDetail — full trade room for Phase E2.
 * Left pane: order summary + payment instructions + action buttons.
 * Right pane: live chat + system events.
 * Live updates over /api/ws/p2p/order/{id}.
 */
export default function P2POrderDetail() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(null); // 'mark-paid' | 'release' | 'cancel'
  const [showRelease, setShowRelease] = useState(false);
  const [showDispute, setShowDispute] = useState(false);
  const [showAddEvidence, setShowAddEvidence] = useState(false);
  const [totp, setTotp] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [paymentProof, setPaymentProof] = useState(""); // data URL of receipt image
  const [proofError, setProofError] = useState("");
  const [actionError, setActionError] = useState("");

  const refresh = async () => {
    try {
      const r = await api.get(`/api/p2p/orders/${orderId}`);
      setOrder(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Live status updates via WebSocket
  useEffect(() => {
    if (!user || !orderId) return;
    const wsRoot = wsBaseUrl();
    if (!wsRoot) return undefined;
    const ws = new WebSocket(`${wsRoot}/api/ws/p2p/order/${orderId}`);
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "status") refresh();
      } catch {}
    };
    ws.onerror = () => {};
    return () => { try { ws.close(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, orderId]);

  if (!order) {
    return <AppShell maxWidth="1100px">
      <div className="p-12 text-center text-muted-foreground">
        {error ? error : <><Loader2 className="animate-spin inline mr-2" size={14}/> Loading order…</>}
      </div>
    </AppShell>;
  }

  const me = user?.id;
  const isBuyer = order.buyer_id === me;
  const isSeller = order.seller_id === me;
  const myRole = isBuyer ? "buyer" : "seller";
  const counterRole = isBuyer ? "seller" : "buyer";

  // Countdown logic
  const expiresAt = order.status === "paid_marked"
    ? (order.release_window_expires_at && new Date(order.release_window_expires_at).getTime())
    : (order.payment_window_expires_at && new Date(order.payment_window_expires_at).getTime());
  const remainingMs = Math.max(0, (expiresAt || 0) - now);
  const min = Math.floor(remainingMs / 60000).toString().padStart(2, "0");
  const sec = Math.floor((remainingMs / 1000) % 60).toString().padStart(2, "0");
  const expired = expiresAt && remainingMs === 0;

  const fmtINR = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  const pm = order.payment_method_snapshot || {};
  const isFinal = ["completed", "cancelled", "refunded"].includes(order.status);

  const STATUS_PILL = {
    in_progress:  ["bg-warning/10 text-warning",  "Awaiting Payment"],
    paid_marked:  ["bg-primary/10 text-primary",     "Awaiting Release"],
    completed:    ["bg-up/10 text-up", "Completed"],
    cancelled:    ["bg-surface-2 text-muted-foreground",    "Cancelled"],
    disputed:     ["bg-down/10 text-down",      "Disputed"],
    refunded:     ["bg-surface-2 text-muted-foreground",    "Refunded"],
  };
  const [pillCls, pillLbl] = STATUS_PILL[order.status] || ["bg-surface-2", order.status];

  // Eligibility for "Open Dispute":
  //   • paid_marked: either party can dispute (release stalled / fake proof)
  //   • completed: only within 24h of completion (chargeback claim)
  //   • in_progress: blocked — use Cancel instead (no funds at risk yet)
  let canDispute = false;
  if (order.status === "paid_marked") canDispute = true;
  else if (order.status === "completed" && order.completed_at) {
    const ageH = (now - new Date(order.completed_at).getTime()) / 3600000;
    canDispute = ageH < 24;
  }

  const onMarkPaid = async () => {
    if (!paymentProof) {
      setActionError("Please upload your payment receipt/screenshot before marking as paid.");
      return;
    }
    setBusy("mark-paid"); setActionError("");
    try {
      await api.post(`/api/p2p/orders/${orderId}/mark-paid`, {
        note: paymentNote || undefined,
        payment_proof_url: paymentProof,
      });
      setPaymentNote(""); setPaymentProof(""); setProofError("");
      await refresh();
    } catch (e) {
      setActionError(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };
  const onRelease = async () => {
    setBusy("release"); setActionError("");
    try {
      await api.post(`/api/p2p/orders/${orderId}/release`,
        totp ? { totp_code: totp } : {});
      setTotp(""); setShowRelease(false);
      await refresh();
    } catch (e) {
      setActionError(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };
  const onCancel = async () => {
    if (!window.confirm("Cancel this order? Crypto returns to the seller's escrow.")) return;
    setBusy("cancel"); setActionError("");
    try {
      await api.post(`/api/p2p/orders/${orderId}/cancel`, { reason: "Cancelled by user" });
      await refresh();
    } catch (e) {
      setActionError(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  return (
    <AppShell maxWidth="1180px">
      <div className="space-y-4">
        <Link to="/p2p" className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground">
          <ArrowLeft size={14} /> Back to marketplace
        </Link>

        <div className="grid lg:grid-cols-[1fr,400px] gap-5">
          {/* ============= LEFT — order details + actions ============= */}
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-cm-navy to-cm-blue text-white flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider opacity-80">Order Reference</div>
                <div className="font-mono text-[14px] font-semibold">{order.order_id}</div>
                <div className="mt-1.5">
                  <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-white/15`}>
                    You are the {myRole}
                  </span>
                </div>
              </div>
              {!isFinal && expiresAt ? (
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-wider opacity-80 flex items-center gap-1 justify-end">
                    <Clock size={11} />
                    {order.status === "paid_marked" ? "Release deadline" : "Pay within"}
                  </div>
                  <div className={`text-[28px] font-semibold tabular-nums ${expired ? "text-red-300" : ""}`}
                       data-testid="order-countdown">
                    {min}:{sec}
                  </div>
                </div>
              ) : (
                <span className={`px-3 py-1 rounded-full text-[12px] font-semibold uppercase tracking-wider ${pillCls}`}>
                  {pillLbl}
                </span>
              )}
            </div>

            {/* Counterparty card (KuCoin-style summary + expandable profile) */}
            <div className="px-6 pt-5">
              <CounterpartyCard
                orderId={order.order_id}
                counterpartyId={isBuyer ? order.seller_id : order.buyer_id}
              />
            </div>

            {/* Body */}
            <div className="p-6 grid md:grid-cols-2 gap-6">
              {/* Order facts */}
              <div className="space-y-2.5 text-[13px]">
                <Row label="Status">
                  <span className={`px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider ${pillCls}`}>{pillLbl}</span>
                </Row>
                <Row label="Crypto" value={`${Number(order.crypto_amount).toFixed(4)} ${order.asset}`} />
                <Row label="Fiat" value={`₹${fmtINR(order.fiat_amount)}`} />
                <Row label="Price" value={`₹${fmtINR(order.price)} per ${order.asset}`} />
                {order.fee_amount_crypto && Number(order.fee_amount_crypto) > 0 && (
                  <Row label="Taker Fee" value={`${order.fee_amount_crypto} ${order.asset}`} />
                )}
              </div>

              {/* Payment instructions (sensitive — shown only to buyer + seller) */}
              <div className="rounded-lg bg-surface-2 p-4 space-y-1.5 text-[13px]">
                <div className="text-[12px] font-semibold text-foreground uppercase tracking-wider mb-1">
                  Pay using {pm.type}
                </div>
                {pm.upi_id && <Row label="UPI ID" value={pm.upi_id} mono />}
                {pm.bank_name && <Row label="Bank" value={pm.bank_name} />}
                {pm.account_number && <Row label="Account #" value={pm.account_number} mono />}
                {pm.ifsc && <Row label="IFSC" value={pm.ifsc} mono />}
                {pm.holder_name && <Row label="Holder" value={pm.holder_name} />}
                <div className="text-[12px] text-muted-foreground pt-2 border-t border-border">
                  Send exactly <b className="text-foreground">₹{fmtINR(order.fiat_amount)}</b> to the above account.
                </div>
              </div>
            </div>

            {/* Action zone */}
            <div className="px-6 py-5 border-t border-border space-y-3">
              {actionError && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-down/10 text-down text-[12px]">
                  <AlertCircle size={14} className="mt-0.5" /> {actionError}
                </div>
              )}

              {/* Buyer: in_progress → Mark Paid */}
              {isBuyer && order.status === "in_progress" && (
                <div className="space-y-2.5">
                  <input
                    type="text"
                    value={paymentNote}
                    onChange={(e) => setPaymentNote(e.target.value)}
                    placeholder="Payment reference / UTR (optional)"
                    maxLength={200}
                    className="w-full px-3 py-2 rounded-lg border border-border text-[13px]"
                    data-testid="payment-note"
                  />
                  <ProofUploader
                    value={paymentProof}
                    onChange={setPaymentProof}
                    error={proofError}
                    onError={setProofError}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={onMarkPaid}
                      disabled={busy === "mark-paid" || !paymentProof}
                      className="flex-1 px-4 py-2.5 rounded-lg bg-up text-white text-[13px] font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
                      data-testid="action-mark-paid"
                      title={!paymentProof ? "Upload payment receipt to enable" : ""}
                    >
                      {busy === "mark-paid" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                      I've Sent the Payment
                    </button>
                    <button
                      onClick={onCancel}
                      disabled={busy === "cancel"}
                      className="px-4 py-2.5 rounded-lg border border-down/30 text-down text-[13px] font-semibold disabled:opacity-50"
                      data-testid="action-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Seller: in_progress → Cancel only */}
              {isSeller && order.status === "in_progress" && (
                <div className="text-[12px] text-muted-foreground">
                  Waiting for the buyer to send ₹{fmtINR(order.fiat_amount)} via {pm.type}…
                  <button
                    onClick={onCancel}
                    disabled={busy === "cancel"}
                    className="ml-3 px-3 py-1.5 rounded-md border border-down/30 text-down text-[12px] font-semibold disabled:opacity-50"
                    data-testid="action-cancel-seller"
                  >
                    Cancel order
                  </button>
                </div>
              )}

              {/* Seller: paid_marked → Release */}
              {isSeller && order.status === "paid_marked" && (
                <div className="space-y-2">
                  <div className="rounded-lg bg-warning/10 text-warning p-3 text-[12px]">
                    ⚠ Buyer claims they've paid. <b>Verify ₹{fmtINR(order.fiat_amount)} arrived in your account</b> before releasing.
                  </div>
                  {order.payment_proof_url && (
                    <ProofViewer url={order.payment_proof_url} note={order.buyer_paid_note} />
                  )}
                  {!showRelease ? (
                    <button
                      onClick={() => {
                        if (user?.totp_enabled) setShowRelease(true);
                        else onRelease();
                      }}
                      disabled={busy === "release"}
                      className="w-full px-4 py-2.5 rounded-lg brand-gradient text-primary-foreground text-[13px] font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
                      data-testid="action-release"
                    >
                      {busy === "release" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                      Release {Number(order.crypto_amount).toFixed(4)} {order.asset}
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={totp}
                        onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                        placeholder="Enter 2FA code"
                        className="w-full px-3 py-2.5 rounded-lg border border-border text-[14px] font-mono tracking-widest text-center"
                        data-testid="release-totp"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setShowRelease(false); setTotp(""); }}
                          className="px-4 py-2 rounded-lg border border-border text-foreground text-[13px] font-semibold"
                          data-testid="release-cancel"
                        >
                          Back
                        </button>
                        <button
                          onClick={onRelease}
                          disabled={busy === "release" || totp.length < 6}
                          className="flex-1 px-4 py-2.5 rounded-lg brand-gradient text-primary-foreground text-[13px] font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
                          data-testid="release-confirm"
                        >
                          {busy === "release" && <Loader2 size={14} className="animate-spin" />}
                          Confirm Release
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Buyer: paid_marked → just wait */}
              {isBuyer && order.status === "paid_marked" && (
                <div className="text-[12px] text-muted-foreground">
                  ⏳ Waiting for the seller to verify your payment and release {order.asset}.
                </div>
              )}

              {/* Final states */}
              {order.status === "completed" && (
                <div className="rounded-lg bg-up/10 text-up p-3 text-[12px]">
                  ✅ Trade completed.{" "}
                  {isBuyer && order.fee_amount_crypto && Number(order.fee_amount_crypto) > 0 && (
                    <> {(Number(order.crypto_amount) - Number(order.fee_amount_crypto)).toFixed(4)} {order.asset} credited to your wallet (taker fee: {order.fee_amount_crypto}).</>
                  )}
                  {!order[isBuyer ? "rating_by_taker" : "rating_by_maker"] && (
                    <RateBlock orderId={order.order_id} onRated={refresh} />
                  )}
                </div>
              )}
              {order.status === "cancelled" && (
                <div className="rounded-lg bg-surface-2 text-muted-foreground p-3 text-[12px]">
                  ❌ Cancelled. {order.cancellation_reason || ""}
                </div>
              )}

              {/* Disputed banner — replaced with full AppealView */}
              {order.status === "disputed" && (
                <AppealView
                  orderId={order.order_id}
                  currentUserId={user?.id}
                  onAddEvidence={() => setShowAddEvidence(true)}
                />
              )}
              {order.status === "refunded" && (
                <div className="rounded-lg bg-surface-2 text-muted-foreground p-3 text-[12px]">
                  ⚖ Refunded to seller via admin dispute resolution.
                </div>
              )}

              {/* Open-dispute button (paid_marked + completed within 24h) */}
              {canDispute && order.status !== "disputed" && (
                <button
                  onClick={() => setShowDispute(true)}
                  className="px-3 py-1.5 rounded-md border border-down/30 text-down text-[12px] font-semibold inline-flex items-center gap-1.5 hover:bg-down/10"
                  data-testid="action-dispute"
                >
                  <Flag size={12} /> Open Dispute
                </button>
              )}
            </div>
          </div>

          {/* ============= RIGHT — chat panel ============= */}
          <ChatPanel orderId={orderId} myRole={myRole} disabled={isFinal} />
        </div>
      </div>

      {showDispute && (
        <DisputeModal
          orderId={orderId}
          orderStatus={order.status}
          myRole={myRole}
          onClose={(refresh) => { setShowDispute(false); if (refresh) refresh(); }}
        />
      )}

      {showAddEvidence && order.dispute_id && (
        <AddEvidenceModal
          disputeId={order.dispute_id}
          onClose={(added) => { setShowAddEvidence(false); if (added) refresh(); }}
        />
      )}
    </AppShell>
  );
}


/* -----------------------------------------------------------
   AddEvidenceModal — for parties to attach more evidence after
   the dispute has already been opened.
   ----------------------------------------------------------- */
function AddEvidenceModal({ disputeId, onClose }) {
  const [evidence, setEvidence] = useState([]);
  const [linkInput, setLinkInput] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pickerError, setPickerError] = useState("");

  const MAX_ITEMS = 10;
  const SIZE_LIMITS = { image: 2 * 1024 * 1024, video: 8 * 1024 * 1024, pdf: 4 * 1024 * 1024 };
  const detectKind = (m) => m?.startsWith("image/") ? "image"
    : m?.startsWith("video/") ? "video"
    : m === "application/pdf" ? "pdf" : null;

  const onPickFiles = async (fileList) => {
    setPickerError("");
    const files = Array.from(fileList || []);
    const room = MAX_ITEMS - evidence.length;
    if (files.length > room) { setPickerError(`Max ${MAX_ITEMS} attachments — ${room} slot(s) left.`); return; }
    const next = [];
    for (const f of files) {
      const kind = detectKind(f.type);
      if (!kind) { setPickerError(`"${f.name}" — unsupported format.`); return; }
      if (f.size > SIZE_LIMITS[kind]) {
        setPickerError(`"${f.name}" is ${(f.size / 1024 / 1024).toFixed(1)}MB — limit for ${kind} is ${SIZE_LIMITS[kind] / 1024 / 1024}MB.`);
        return;
      }
      try {
        const url = kind === "image"
          ? await fileToResizedDataUrl(f, 1600, 0.78)
          : await readFileAsDataUrl(f);
        next.push({ kind, name: f.name, url, size: f.size });
      } catch { setPickerError(`Couldn't read "${f.name}".`); return; }
    }
    setEvidence([...evidence, ...next]);
  };

  const addLink = () => {
    const v = linkInput.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) { setPickerError("Link must start with http:// or https://"); return; }
    if (evidence.length >= MAX_ITEMS) { setPickerError(`Max ${MAX_ITEMS} attachments.`); return; }
    setEvidence([...evidence, { kind: "link", name: v, url: v }]);
    setLinkInput(""); setPickerError("");
  };

  const removeAt = (i) => setEvidence(evidence.filter((_, idx) => idx !== i));

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const urls = evidence.map((e) => e.url).filter(Boolean);
      if (urls.length === 0) { setError("Attach at least one item."); setBusy(false); return; }
      await api.post(`/api/p2p/disputes/${disputeId}/evidence`, {
        evidence_urls: urls,
        note: note.trim() || `Added ${urls.length} item(s) of additional evidence.`,
      });
      onClose(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
      onClick={() => onClose(false)}
      data-testid="add-evidence-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[520px] bg-card rounded-2xl shadow-2xl border border-border max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-foreground inline-flex items-center gap-2">
            <ImageIcon size={14} className="text-primary" /> Add More Evidence
          </h3>
          <button onClick={() => onClose(false)} className="text-muted-foreground hover:text-foreground" aria-label="Close">
            <XCircle size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-[12px] text-muted-foreground">
            Attach screenshots, videos or PDFs to strengthen your case. Up to {MAX_ITEMS} items.
          </div>

          <label className="block">
            <input
              type="file"
              accept="image/*,video/mp4,video/quicktime,video/webm,application/pdf"
              multiple
              onChange={(e) => onPickFiles(e.target.files)}
              className="hidden"
              data-testid="add-evidence-file"
            />
            <span className={
              "flex items-center justify-center gap-2 px-3 py-3 rounded-lg border-2 border-dashed cursor-pointer text-[12.5px] font-semibold transition " +
              (evidence.length >= MAX_ITEMS
                ? "border-border text-muted-foreground cursor-not-allowed"
                : "border-border text-muted-foreground hover:border-primary hover:text-primary")
            }>
              <ImageIcon size={14} />
              {evidence.length >= MAX_ITEMS
                ? `Maximum ${MAX_ITEMS} attachments reached`
                : "Click to attach screenshot / video / PDF"}
            </span>
          </label>

          <div className="flex gap-2">
            <input
              type="url"
              value={linkInput}
              onChange={(e) => setLinkInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLink(); } }}
              placeholder="…or paste a public link"
              className="flex-1 px-3 py-2 rounded-lg border border-border text-[12.5px]"
              data-testid="add-evidence-link-input"
            />
            <button
              onClick={addLink}
              disabled={!linkInput.trim() || evidence.length >= MAX_ITEMS}
              className="px-3 py-2 rounded-lg border border-border text-foreground text-[12px] font-semibold disabled:opacity-50"
            >
              Add link
            </button>
          </div>

          {pickerError && <div className="text-[11.5px] text-down">{pickerError}</div>}

          {evidence.length > 0 && (
            <div className="space-y-1.5">
              {evidence.map((ev, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-md border border-border bg-surface-2">
                  {ev.kind === "image" && <img src={ev.url} alt="" className="w-9 h-9 rounded object-cover bg-card" />}
                  {ev.kind === "video" && <span className="w-9 h-9 rounded grid place-items-center brand-gradient text-primary-foreground text-[10px] font-semibold">VID</span>}
                  {ev.kind === "pdf" && <span className="w-9 h-9 rounded grid place-items-center bg-down text-white text-[10px] font-semibold">PDF</span>}
                  {ev.kind === "link" && <span className="w-9 h-9 rounded grid place-items-center brand-gradient text-primary-foreground text-[10px] font-semibold">URL</span>}
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] text-foreground truncate font-medium">{ev.name}</div>
                    <div className="text-[10.5px] text-muted-foreground">
                      {ev.kind === "link" ? "External link" : `${ev.kind.toUpperCase()} · ${(ev.size / 1024).toFixed(0)} KB`}
                    </div>
                  </div>
                  <button onClick={() => removeAt(i)} className="p-1.5 text-down hover:bg-down/10 rounded" aria-label="Remove">
                    <XCircle size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={400}
            placeholder="Note for the admin (optional)…"
            className="w-full px-3 py-2 rounded-lg border border-border text-[12.5px]"
          />

          {error && <div className="text-[12px] text-down">{error}</div>}
        </div>
        <div className="px-5 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={() => onClose(false)} className="px-4 py-2 rounded-lg border border-border text-foreground text-[12.5px] font-semibold">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || evidence.length === 0}
            className="px-4 py-2 rounded-lg brand-gradient text-primary-foreground text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="add-evidence-submit"
          >
            {busy && <Loader2 size={13} className="animate-spin" />}
            Submit Evidence
          </button>
        </div>
      </div>
    </div>
  );
}


function DisputeModal({ orderId, orderStatus, myRole, onClose }) {
  const [reason, setReason] = useState(myRole === "buyer" ? "seller_no_release" : "fake_proof");
  const [description, setDescription] = useState("");
  // Each evidence item: { kind: "file"|"link", name, mime, url (data URL or http URL), size }
  const [evidence, setEvidence] = useState([]);
  const [linkInput, setLinkInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pickerError, setPickerError] = useState("");

  // Reasons available depend on role + status. Keep it self-explanatory.
  const REASONS = myRole === "buyer"
    ? [
        ["seller_no_release", "Seller hasn't released crypto"],
        ["wrong_amount",      "Disputed amount / partial transfer"],
        ["other",             "Other"],
      ]
    : [
        ["buyer_no_payment",  "Buyer didn't actually pay"],
        ["fake_proof",        "Buyer submitted fake / forged proof"],
        ["chargeback",        "Payment was reversed (chargeback)"],
        ["wrong_amount",      "Wrong amount received"],
        ["other",             "Other"],
      ];

  const MAX_ITEMS = 10;
  const SIZE_LIMITS = {
    image: 2 * 1024 * 1024,   // 2MB raw → resized to ~150KB
    video: 8 * 1024 * 1024,   // 8MB
    pdf:   4 * 1024 * 1024,   // 4MB
  };

  const detectKind = (mime) => {
    if (!mime) return null;
    if (mime.startsWith("image/")) return "image";
    if (mime.startsWith("video/")) return "video";
    if (mime === "application/pdf") return "pdf";
    return null;
  };

  const onPickFiles = async (fileList) => {
    setPickerError("");
    const files = Array.from(fileList || []);
    const room = MAX_ITEMS - evidence.length;
    if (files.length > room) {
      setPickerError(`Max ${MAX_ITEMS} attachments — ${room} slot(s) left.`);
      return;
    }
    const next = [];
    for (const f of files) {
      const kind = detectKind(f.type);
      if (!kind) {
        setPickerError(`"${f.name}" — unsupported format. Use JPG / PNG / WEBP / MP4 / MOV / WEBM / PDF.`);
        return;
      }
      const limit = SIZE_LIMITS[kind];
      if (f.size > limit) {
        setPickerError(`"${f.name}" is ${(f.size / 1024 / 1024).toFixed(1)}MB — limit for ${kind} is ${limit / 1024 / 1024}MB.`);
        return;
      }
      try {
        const dataUrl = kind === "image"
          ? await fileToResizedDataUrl(f, 1600, 0.78)
          : await readFileAsDataUrl(f);
        next.push({ kind, name: f.name, mime: f.type, url: dataUrl, size: f.size, source: "file" });
      } catch {
        setPickerError(`Couldn't read "${f.name}".`);
        return;
      }
    }
    setEvidence([...evidence, ...next]);
  };

  const addLink = () => {
    const v = linkInput.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) {
      setPickerError("Link must start with http:// or https://");
      return;
    }
    if (evidence.length >= MAX_ITEMS) {
      setPickerError(`Max ${MAX_ITEMS} attachments.`);
      return;
    }
    setEvidence([...evidence, { kind: "link", name: v, url: v, source: "link" }]);
    setLinkInput("");
    setPickerError("");
  };

  const removeAt = (i) => setEvidence(evidence.filter((_, idx) => idx !== i));

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const evidenceClean = evidence.map((e) => e.url).filter(Boolean);
      await api.post(`/api/p2p/orders/${orderId}/dispute`, {
        reason, description, evidence_urls: evidenceClean,
      });
      onClose(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => onClose(false)}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[560px] bg-card rounded-2xl shadow-2xl border border-border max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="text-[17px] font-semibold text-foreground flex items-center gap-2">
            <Flag size={16} className="text-down" /> Open Dispute
          </h3>
          <button onClick={() => onClose(false)} className="p-1 text-muted-foreground hover:bg-surface-2 rounded" data-testid="dispute-close">
            <XCircle size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-lg bg-warning/10 text-warning p-3 text-[12px]">
            ⚠ Opening a dispute freezes the order. Funds remain in escrow until an admin
            rules — typically within 2 hours. False disputes may result in a P2P ban.
          </div>

          <div className="space-y-1.5">
            <label className="text-[12px] font-semibold text-foreground uppercase tracking-wider">Reason</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-[14px]"
              data-testid="dispute-reason"
            >
              {REASONS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-[12px] font-semibold text-foreground uppercase tracking-wider">
              Description ({description.length}/1000)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              minLength={10}
              maxLength={1000}
              placeholder="Explain what happened in detail. Include timestamps, transaction IDs, and any context the admin will need."
              className="w-full px-3 py-2 rounded-lg border border-border text-[13px]"
              data-testid="dispute-description"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[12px] font-semibold text-foreground uppercase tracking-wider">
              Evidence ({evidence.length}/{MAX_ITEMS})
            </label>
            <p className="text-[11.5px] text-muted-foreground -mt-1">
              Upload screenshots, videos or PDFs (bank statement, payment confirmation, chat history). Or paste a public link.
            </p>

            {/* Picker */}
            <label className="block">
              <input
                type="file"
                accept="image/*,video/mp4,video/quicktime,video/webm,application/pdf"
                multiple
                onChange={(e) => onPickFiles(e.target.files)}
                className="hidden"
                data-testid="dispute-evidence-file"
              />
              <span
                className={
                  "flex items-center justify-center gap-2 px-3 py-3 rounded-lg border-2 border-dashed cursor-pointer text-[12.5px] font-semibold transition " +
                  (evidence.length >= MAX_ITEMS
                    ? "border-border text-muted-foreground cursor-not-allowed"
                    : "border-border text-muted-foreground hover:border-primary hover:text-primary")
                }
              >
                <ImageIcon size={14} />
                {evidence.length >= MAX_ITEMS
                  ? `Maximum ${MAX_ITEMS} attachments reached`
                  : "Click to attach screenshot / video / PDF"}
              </span>
            </label>

            {/* Or paste a link */}
            <div className="flex gap-2">
              <input
                type="url"
                value={linkInput}
                onChange={(e) => setLinkInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLink(); } }}
                placeholder="…or paste a link (https://imgur.com/…)"
                className="flex-1 px-3 py-2 rounded-lg border border-border text-[12.5px]"
                data-testid="dispute-evidence-link-input"
              />
              <button
                onClick={addLink}
                disabled={!linkInput.trim() || evidence.length >= MAX_ITEMS}
                className="px-3 py-2 rounded-lg border border-border text-foreground text-[12px] font-semibold disabled:opacity-50"
                data-testid="dispute-evidence-link-add"
              >
                Add link
              </button>
            </div>

            {pickerError && (
              <div className="text-[11.5px] text-down">{pickerError}</div>
            )}

            {/* Attached items */}
            {evidence.length > 0 && (
              <div className="space-y-1.5" data-testid="dispute-evidence-list">
                {evidence.map((ev, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 p-2 rounded-md border border-border bg-surface-2"
                    data-testid={`dispute-evidence-item-${i}`}
                  >
                    {ev.kind === "image" && (
                      <img src={ev.url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0 bg-card" />
                    )}
                    {ev.kind === "video" && (
                      <span className="w-10 h-10 rounded grid place-items-center brand-gradient text-primary-foreground text-[10px] font-semibold flex-shrink-0">VID</span>
                    )}
                    {ev.kind === "pdf" && (
                      <span className="w-10 h-10 rounded grid place-items-center bg-down text-white text-[10px] font-semibold flex-shrink-0">PDF</span>
                    )}
                    {ev.kind === "link" && (
                      <span className="w-10 h-10 rounded grid place-items-center brand-gradient text-primary-foreground text-[10px] font-semibold flex-shrink-0">URL</span>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] text-foreground truncate font-medium">
                        {ev.name}
                      </div>
                      <div className="text-[10.5px] text-muted-foreground">
                        {ev.kind === "link"
                          ? "External link"
                          : `${ev.kind.toUpperCase()} · ${(ev.size / 1024).toFixed(0)} KB`}
                      </div>
                    </div>
                    <button
                      onClick={() => removeAt(i)}
                      className="p-1.5 text-down hover:bg-down/10 rounded"
                      aria-label="Remove evidence"
                      data-testid={`dispute-evidence-remove-${i}`}
                    >
                      <XCircle size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-down/10 text-down text-[12px]">
              <AlertCircle size={14} className="mt-0.5" /> {error}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={() => onClose(false)} className="px-4 py-2 rounded-lg border border-border text-foreground text-[13px] font-semibold">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || description.trim().length < 10}
            className="px-5 py-2 rounded-lg bg-down text-white text-[13px] font-semibold disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="dispute-submit"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Submit Dispute
          </button>
        </div>
      </div>
    </div>
  );
}


/* -----------------------------------------------------------
   Sub-components
   ----------------------------------------------------------- */

/** Resize image client-side and emit a JPEG data URL (~50-150KB typical). */
async function fileToResizedDataUrl(file, maxDim = 1280, quality = 0.78) {
  const bitmap = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => {
      const img = new Image();
      img.onload = () => res(img);
      img.onerror = rej;
      img.src = r.result;
    };
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const ratio = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * ratio);
  const h = Math.round(bitmap.height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

/** Read any file (video, PDF, etc.) as a base64 data URL. */
function readFileAsDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function ProofUploader({ value, onChange, error, onError }) {
  const onPick = async (e) => {
    onError("");
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      onError("Please choose an image file (PNG, JPG or WEBP).");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      onError("Image is larger than 8MB. Please pick a smaller one.");
      return;
    }
    try {
      const url = await fileToResizedDataUrl(f);
      onChange(url);
    } catch {
      onError("Couldn't read this image. Please try a different file.");
    }
  };

  return (
    <div className="space-y-1.5">
      <label className="block">
        <input
          type="file"
          accept="image/*"
          onChange={onPick}
          className="hidden"
          data-testid="payment-proof-input"
        />
        <span className={
          "flex items-center justify-center gap-2 px-3 py-3 rounded-lg border-2 border-dashed cursor-pointer text-[12.5px] font-semibold transition " +
          (value
            ? "border-up/30 bg-up/10 text-up"
            : "border-border text-muted-foreground hover:border-primary hover:text-primary")
        }>
          <ImageIcon size={14} />
          {value ? "Receipt attached — click to replace" : "Upload payment receipt / screenshot (required)"}
        </span>
      </label>
      {value && (
        <div className="rounded-lg overflow-hidden border border-border bg-surface-2">
          <img
            src={value}
            alt="Payment receipt preview"
            className="w-full max-h-[180px] object-contain bg-card"
            data-testid="payment-proof-preview"
          />
          <div className="flex items-center justify-between px-2.5 py-1.5">
            <span className="text-[11px] text-muted-foreground">Receipt preview</span>
            <button
              onClick={() => onChange("")}
              className="text-[11px] text-down font-semibold"
              data-testid="payment-proof-clear"
            >
              Remove
            </button>
          </div>
        </div>
      )}
      {error && <div className="text-[11.5px] text-down">{error}</div>}
    </div>
  );
}

function ProofViewer({ url, note }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-lg border border-border bg-surface-2 p-2.5 space-y-2"
      data-testid="proof-viewer"
    >
      <div className="text-[11.5px] font-semibold text-foreground uppercase tracking-wider">
        Buyer's Receipt
      </div>
      <button
        onClick={() => setOpen(true)}
        className="block w-full rounded-md overflow-hidden border border-border hover:border-primary transition"
        title="Click to enlarge"
      >
        <img
          src={url}
          alt="Buyer's payment receipt"
          className="w-full max-h-[200px] object-contain bg-card"
          data-testid="proof-viewer-thumb"
        />
      </button>
      {note && (
        <div className="text-[12px] text-muted-foreground">
          <span className="font-semibold text-foreground">Reference:</span> {note}
        </div>
      )}
      {open && (
        <div
          className="fixed inset-0 z-[90] bg-black/80 grid place-items-center p-4"
          onClick={() => setOpen(false)}
        >
          <img
            src={url}
            alt="Receipt full view"
            className="max-w-[92vw] max-h-[92vh] rounded-md shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}

function Row({ label, value, children, mono }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      {children ?? (
        <span className={`text-foreground font-semibold ${mono ? "font-mono text-[12px]" : ""}`}>{value}</span>
      )}
    </div>
  );
}

function ChatPanel({ orderId, myRole, disabled }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [attachment, setAttachment] = useState("");
  const [busy, setBusy] = useState(false);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 50);
  };

  const load = async () => {
    try {
      const r = await api.get(`/api/p2p/orders/${orderId}/messages`);
      setMessages(r.data.messages);
      scrollToBottom();
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  // WS listener
  useEffect(() => {
    const wsRoot = wsBaseUrl();
    if (!wsRoot) return undefined;
    const ws = new WebSocket(`${wsRoot}/api/ws/p2p/order/${orderId}`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "message") {
          setMessages((m) => [...m, data]);
          scrollToBottom();
        }
      } catch {}
    };
    return () => { try { ws.close(); } catch {} };
  }, [orderId]);

  const send = async () => {
    if (!body.trim() && !attachment.trim()) return;
    setBusy(true);
    try {
      await api.post(`/api/p2p/orders/${orderId}/messages`, {
        body: body.trim() || undefined,
        attachment_url: attachment.trim() || undefined,
      });
      setBody(""); setAttachment("");
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card rounded-2xl border border-border flex flex-col h-[640px]">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <h3 className="text-[14px] font-semibold text-foreground">Order Chat</h3>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">End-to-end logged</span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2.5 text-[13px]">
        {messages.length === 0 ? (
          <div className="text-center text-muted-foreground text-[12px] py-6">
            No messages yet. Use chat to coordinate the payment with your counterparty.
          </div>
        ) : messages.map((m) => {
          if (m.sender_role === "system") {
            return (
              <div key={m.message_id} className="flex justify-center" data-testid={`msg-${m.message_id}`}>
                <div className="px-3 py-1.5 rounded-full bg-warning/10 text-warning text-[11px] max-w-[85%] text-center">
                  {m.body}
                </div>
              </div>
            );
          }
          const isMe = m.sender_role === myRole;
          return (
            <div key={m.message_id} className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.message_id}`}>
              <div className={`max-w-[80%] rounded-lg px-3.5 py-2 ${
                isMe ? "brand-gradient text-primary-foreground" : "bg-surface-2 text-foreground"
              }`}>
                {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                {m.attachment_url && (
                  <a href={m.attachment_url} target="_blank" rel="noreferrer"
                     className={`mt-1 inline-flex items-center gap-1 text-[11px] underline ${isMe ? "text-white/90" : "text-primary"}`}>
                    <ImageIcon size={12} /> Attachment
                  </a>
                )}
                <div className={`text-[10px] mt-1 ${isMe ? "text-white/70" : "text-muted-foreground"}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t border-border p-3 space-y-2">
        {attachment && (
          <div className="flex items-center gap-2 px-2 py-1 rounded bg-surface-2 text-[11px]">
            <ImageIcon size={11} /> {attachment}
            <button onClick={() => setAttachment("")} className="ml-auto text-muted-foreground hover:text-down">
              <XCircle size={12} />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={disabled ? "Order is finalized — chat closed" : "Type a message…"}
            disabled={disabled}
            rows={2}
            maxLength={2000}
            className="flex-1 px-3 py-2 rounded-lg border border-border text-[13px] resize-none disabled:bg-surface-2 disabled:text-muted-foreground"
            data-testid="chat-input"
          />
          <button
            onClick={() => {
              const url = window.prompt("Paste a public attachment URL (e.g. screenshot):");
              if (url) setAttachment(url.trim());
            }}
            disabled={disabled}
            className="p-2.5 rounded-lg border border-border text-foreground disabled:opacity-50"
            title="Add attachment"
            data-testid="chat-attach"
          >
            <ImageIcon size={14} />
          </button>
          <button
            onClick={send}
            disabled={busy || disabled || (!body.trim() && !attachment.trim())}
            className="px-4 py-2.5 rounded-lg brand-gradient text-primary-foreground font-semibold disabled:opacity-50 inline-flex items-center gap-1 text-[12px]"
            data-testid="chat-send"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          </button>
        </div>
      </div>
    </div>
  );
}

function RateBlock({ orderId, onRated }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!rating) return;
    setBusy(true);
    try {
      await api.post(`/api/p2p/orders/${orderId}/rate`, { rating, comment: comment || undefined });
      onRated?.();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-up/30 space-y-2">
      <div className="text-[12px] font-semibold text-foreground">Rate your counterparty</div>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            onClick={() => setRating(n)}
            data-testid={`rate-${n}`}
            className={n <= rating ? "text-warning" : "text-muted-foreground"}
          >
            <Star size={20} fill={n <= rating ? "currentColor" : "none"} />
          </button>
        ))}
      </div>
      <input
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Quick comment (optional)"
        maxLength={200}
        className="w-full px-3 py-1.5 rounded-md border border-up/30 text-[12px]"
        data-testid="rate-comment"
      />
      <button
        onClick={submit}
        disabled={!rating || busy}
        className="px-3 py-1.5 rounded-md brand-gradient text-primary-foreground text-[12px] font-semibold disabled:opacity-50"
        data-testid="rate-submit"
      >
        Submit Rating
      </button>
    </div>
  );
}
